import { useState } from 'react'
import { Stack, Text, IconButton, DetailsList, IColumn, SelectionMode, DetailsListLayoutMode } from '@fluentui/react'
import { ActiveUsersResponse, ActiveUser } from '../../api/models'

type ActiveUsersTableProps = {
  data: ActiveUsersResponse | null
}

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flex: 1,
  },
}

const PAGE_SIZE = 10

const ActiveUsersTable = ({ data }: ActiveUsersTableProps) => {
  const [sortColumn, setSortColumn] = useState<string>('conversation_count')
  const [sortDescending, setSortDescending] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)

  if (!data) return null

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDescending(!sortDescending)
    } else {
      setSortColumn(column)
      setSortDescending(true)
    }
    setCurrentPage(1)
  }

  const sortedUsers = [...data.users].sort((a, b) => {
    const key = sortColumn as keyof ActiveUser
    const aVal = a[key]
    const bVal = b[key]
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDescending ? bVal - aVal : aVal - bVal
    }
    const aStr = String(aVal ?? '').toLowerCase()
    const bStr = String(bVal ?? '').toLowerCase()
    return sortDescending ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr)
  })

  const totalPages = Math.ceil(sortedUsers.length / PAGE_SIZE)
  const startIdx = (currentPage - 1) * PAGE_SIZE
  const pagedUsers = sortedUsers.slice(startIdx, startIdx + PAGE_SIZE)

  const formatDate = (iso?: string) => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return iso
    }
  }

  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 140,
      maxWidth: 220,
      isSorted: sortColumn === 'name',
      isSortedDescending: sortColumn === 'name' && sortDescending,
      iconName: sortColumn !== 'name' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('name'),
    },
    {
      key: 'email',
      name: 'Email',
      fieldName: 'email',
      minWidth: 180,
      maxWidth: 300,
      isSorted: sortColumn === 'email',
      isSortedDescending: sortColumn === 'email' && sortDescending,
      iconName: sortColumn !== 'email' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('email'),
    },
    {
      key: 'conversation_count',
      name: 'Conversations',
      fieldName: 'conversation_count',
      minWidth: 100,
      maxWidth: 130,
      isSorted: sortColumn === 'conversation_count',
      isSortedDescending: sortColumn === 'conversation_count' && sortDescending,
      iconName: sortColumn !== 'conversation_count' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('conversation_count'),
    },
    {
      key: 'questions_asked',
      name: 'Questions Asked',
      fieldName: 'questions_asked',
      minWidth: 110,
      maxWidth: 140,
      isSorted: sortColumn === 'questions_asked',
      isSortedDescending: sortColumn === 'questions_asked' && sortDescending,
      iconName: sortColumn !== 'questions_asked' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('questions_asked'),
    },
    {
      key: 'first_active',
      name: 'First Active',
      fieldName: 'first_active',
      minWidth: 110,
      maxWidth: 140,
      isSorted: sortColumn === 'first_active',
      isSortedDescending: sortColumn === 'first_active' && sortDescending,
      iconName: sortColumn !== 'first_active' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('first_active'),
      onRender: (item: ActiveUser) => formatDate(item.first_active),
    },
    {
      key: 'last_active',
      name: 'Last Active',
      fieldName: 'last_active',
      minWidth: 110,
      maxWidth: 140,
      isSorted: sortColumn === 'last_active',
      isSortedDescending: sortColumn === 'last_active' && sortDescending,
      iconName: sortColumn !== 'last_active' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('last_active'),
      onRender: (item: ActiveUser) => formatDate(item.last_active),
    },
  ]

  return (
    <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
          Active Users
        </Text>
        <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
          {sortedUsers.length > PAGE_SIZE
            ? `Showing ${startIdx + 1}-${Math.min(startIdx + PAGE_SIZE, sortedUsers.length)} of ${sortedUsers.length} users`
            : `${data.total_active_users} user${data.total_active_users !== 1 ? 's' : ''}`}
        </Text>
      </Stack>
      {data.users.length > 0 ? (
        <>
          <DetailsList
            items={pagedUsers}
            columns={columns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            isHeaderVisible={true}
            onShouldVirtualize={() => false}
          />
          <Stack
            horizontal
            horizontalAlign="center"
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { paddingTop: 12 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              ariaLabel="Previous page"
            />
            <Text variant="medium">
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              ariaLabel="Next page"
            />
          </Stack>
        </>
      ) : (
        <Text styles={{ root: { color: '#a19f9d', textAlign: 'center', padding: 40 } }}>
          No active users found for this period.
        </Text>
      )}
    </Stack>
  )
}

export default ActiveUsersTable
